import type { ReactElement } from 'react';

interface SparklineProps {
    /** Ordered series values; fewer than two points renders nothing. */
    values: number[];
    /** Stroke/area color (a theme token or CSS color). */
    color: string;
    /** SVG height in px. */
    height?: number;
}

/**
 * A minimal, dependency-free time-series sparkline: a normalized line with a
 * faint filled area. Scales to its container width via a fixed viewBox, so it
 * drops cleanly into a KPI tile, a trend card, or a ranked-list cell.
 */
export default function Sparkline({ values, color, height = 34 }: SparklineProps): ReactElement | null {
    if (values.length < 2) return null;
    const w = 120;
    const h = height;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const point = (v: number, i: number): [number, number] => [(i / (values.length - 1)) * w, h - ((v - min) / range) * (h - 4) - 2];
    const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${point(v, i)[0].toFixed(1)} ${point(v, i)[1].toFixed(1)}`).join(' ');
    const area = `${line} L ${w} ${h} L 0 ${h} Z`;
    return (
        <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
            <path d={area} fill={color} opacity={0.1} />
            <path d={line} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        </svg>
    );
}
